import { useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, Trophy, Megaphone, Image, Settings, LogOut, Crown, Menu, X, ExternalLink, UserPlus, Wallet, Swords } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/admin/resellers", label: "Resellers", Icon: Users },
  { to: "/admin/rankings", label: "Top Agency Ranking", Icon: Trophy },
  { to: "/admin/notices", label: "Notices", Icon: Megaphone },
  { to: "/admin/banners", label: "Banners / Images", Icon: Image },
  { to: "/admin/host-applications", label: "Host Applications", Icon: UserPlus },
  { to: "/admin/pk-battles", label: "PK Battles", Icon: Swords },
  { to: "/admin/salaries", label: "Official Host Salary", Icon: Wallet },
  { to: "/admin/settings", label: "Website Settings", Icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-obsidian text-slate-400">Loading...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <>
      <Link to="/admin" className="flex items-center gap-2.5 px-6 py-6">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg gold-gradient">
          <Crown className="h-5 w-5 text-obsidian" />
        </span>
        <span className="font-display text-lg gold-text">Golden Live</span>
      </Link>
      <nav className="flex-1 space-y-1 px-3">
        {nav.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
            data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "gold-gradient text-obsidian" : "text-slate-300 hover:bg-white/5 hover:text-gold"
              }`
            }>
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="space-y-1 border-t border-gold/10 px-3 py-4">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-gold">
          <ExternalLink className="h-5 w-5" /> View Website
        </a>
        <button onClick={doLogout} data-testid="admin-logout-btn"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10">
          <LogOut className="h-5 w-5" /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Mobile top bar */}
      <div className="glass-nav sticky top-0 z-40 flex items-center justify-between px-4 py-3 lg:hidden">
        <span className="font-display text-lg gold-text">Golden Live Admin</span>
        <button onClick={() => setOpen(true)} className="text-gold" data-testid="admin-mobile-menu"><Menu className="h-6 w-6" /></button>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-gold/10 bg-obsidian-card lg:flex">
          <SidebarContent />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <aside className="absolute inset-y-0 left-0 flex w-72 flex-col border-r border-gold/10 bg-obsidian-card">
              <button onClick={() => setOpen(false)} className="absolute right-3 top-3 text-slate-400"><X className="h-6 w-6" /></button>
              <SidebarContent />
            </aside>
          </div>
        )}

        <main className="flex-1 lg:ml-64">
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
